/**
 * Where to drop a nurse who reopens the identity-verification flow.
 *
 * Pure, no React and no imports, so the unit tests can load it without the
 * React Native runtime.
 *
 * The flow has three steps: 0 selfie, 1 photo ID, 2 verification result.
 *
 * This used to key on the SELFIE alone:
 *
 *     if (selfieStatus === "approved") { setApproved(true); setStepIdx(2); }
 *
 * which is only right when the two always agree. They do not. Jennifer Halpin
 * photographed herself into the photo-ID slot; the selfie passed and the ID was
 * rejected as "a selfie taken in a vehicle, not a government-issued photo ID".
 * Every time she reopened the flow she was marked complete and shown "Identity
 * Confirmed" with nothing but a Done button — no way back to the upload she
 * needed to redo. The result screen then skips its own status check whenever it
 * starts approved, so nothing later corrected it either.
 *
 * The flow is finished only when BOTH halves are approved. A rejection sends
 * her back to the step that produced it, which is the only step that can fix it.
 */

/** Submission statuses this resolver cares about; anything else is "in progress". */
export type IdentityStatus = string | null | undefined;

export interface IdentityResumeState {
    /** 0 = selfie, 1 = photo ID, 2 = verification result. */
    stepIdx: 0 | 1 | 2;
    /** True only when the whole flow is genuinely done. */
    approved: boolean;
}

const isPending = (s: IdentityStatus): boolean => s === "submitted" || s === "ai_verifying";

export function resolveIdentityResumeState(
    selfieStatus: IdentityStatus,
    documentStatus: IdentityStatus
): IdentityResumeState {
    //  Done means both halves passed. Never the selfie on its own.
    if (selfieStatus === "approved" && documentStatus === "approved") {
        return { stepIdx: 2, approved: true };
    }

    //  Send her to the step that failed, so the fix is the next thing she sees.
    //  Selfie first: it is step 0, and redoing it re-runs the comparison anyway.
    if (selfieStatus === "rejected" || selfieStatus === "verification_failed") {
        return { stepIdx: 0, approved: false };
    }
    if (documentStatus === "rejected" || documentStatus === "verification_failed") {
        return { stepIdx: 1, approved: false };
    }

    //  Still being checked — the result screen polls and will resolve it.
    if (isPending(selfieStatus) && (isPending(documentStatus) || documentStatus === "approved")) {
        return { stepIdx: 2, approved: false };
    }

    //  Selfie is in, the ID has not been provided yet.
    if (isPending(selfieStatus) || selfieStatus === "approved") {
        return { stepIdx: 1, approved: false };
    }

    return { stepIdx: 0, approved: false };
}
